/*
================================= LIQUID SURCHARGE ==============================================

A buyer-facing percentage carried on the product bank as `liquid_surcharge` and totalled onto
the order as `liquid_surcharge_amt`. The buyer sees it as "Liquid delivery charge".

THIS IS A PREVIEW OF THE API'S FIGURE, NOT THE FIGURE ITSELF.
The authoritative amount is computed at order placement by the API and stored on the order
(medimny-api lib/liquidSurcharge.js). What this file does is reproduce that arithmetic in the cart
so the buyer can see the charge before they commit to it - the same relationship the cart's GST and
coupon previews already have with the API. If the formula changes on the API side it MUST change
here too, or the cart will quote one number and the order will charge another.

THE ARITHMETIC
  Per line, on the same ex-GST base GST is charged on, then summed:

      line surcharge = ex-GST line value  x  (rate / 100)

  "Ex-GST line value" is `singleItemTotalPrice` in the cart - quantity x ePTR with bonus/free units
  already folded in for Same / SameAndDiscount discounts. That is the identical figure the API calls
  `product_cost` at placement.

  No GST is charged on the surcharge, and it is NOT part of the items total. It is added to the
  payable alongside the delivery charge:

      payable = items total + GST + delivery charge + liquid surcharge - product coupons

WHERE THE RATE COMES FROM
  `Inventory.product_id.liquid_surcharge` on the raw cart response. The API only ever stores a
  non-zero rate on a product whose type is allowed one - every type except Capsule and Tablet
  (controllers/product.js) - so a non-zero rate is sufficient on its own here, and the cart never
  has to know anything about product types.
*/

// Clamped to the 0..10 the admin form offers and the API enforces, so a value that somehow got past
// both cannot overcharge in the preview either. A missing / non-numeric rate reads as 0.
export const liquidSurchargeRate = (product) => {
  const rate = Number(product && product.liquid_surcharge);
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return Math.min(rate, 10);
};

/*
The surcharge for ONE cart line, in rupees.

`exGstLineTotal` is that line's ex-GST value with bonus units already folded in (the cart's
`singleItemTotalPrice`). Returns 0 for a line carrying no rate, so callers can add it
unconditionally the way they add GST.
*/
export const liquidSurchargeForLine = (rate, exGstLineTotal) => {
  const pct = Number(rate);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  const base = Number(exGstLineTotal);
  if (!Number.isFinite(base) || base <= 0) return 0;
  return (base * Math.min(pct, 10)) / 100;
};

export default liquidSurchargeForLine;
